import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  InputLabel,
  OutlinedInput,
  Stack,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import PosterImagePack from "../../Misc/PosterImagePack";
import { thumbnailService } from "../../../Core/Api/services";
import { ThumbnailSize } from "../../../Core/Api/Thumbnail/thumbnail-api-service";
import { DbCultureInfos } from "../../../Core/Api/Types/db-culture-infos";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import { useDispatch } from "react-redux";
import {
  AddCultures,
  AddTitleCultures,
  GetCulturesList,
  GetEditTitle,
  GetIsRightPanelOpen,
  GetPackageCultures,
  GetSelectedPackageIndex,
  GetSelectedTitle,
  GetTitleCultures,
  SetIsRightPanelOpen,
} from "../../../Redux/Reducers/redux-slice-bundle-maker";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

interface BundleMakerSubbarProps {
  displayedTitle: string;
  //setRightPanelOpen;
  //isRightPanelOpen: boolean;
  fetchMeta: (
    objectId: number,
    broadcasterId: number | undefined | null,
    cultureIds: number[],
    addToCurrentMeta: boolean
  ) => void;
  selectedBroadcasterId: number | undefined;
  selectedPackageId: number | undefined;
  handleEditTitle: () => void;
  isLoading: boolean;
  isSaving: boolean;
}

const BundleMakerSubbar: React.FC<BundleMakerSubbarProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const { displayedTitle, fetchMeta, selectedBroadcasterId, selectedPackageId, handleEditTitle, isLoading, isSaving } =
    props;

  //------local state/select store values------

  const titleId = useSelector(GetSelectedTitle)?.id;
  const selectedPackageIndex = useSelector(GetSelectedPackageIndex);
  const packageCultures = useSelector(GetPackageCultures);
  const titleCultures = useSelector(GetTitleCultures);
  const cultures = useSelector(GetCulturesList);
  const isRightPanelOpen = useSelector(GetIsRightPanelOpen);
  const editTitle = useSelector(GetEditTitle);

  const [cultureToAdd, setCultureToAdd] = React.useState<DbCultureInfos[]>([]);

  // add culture to current package or title
  const handleClickAdd = () => {
    editTitle ? dispatch(AddTitleCultures(cultureToAdd)) : dispatch(AddCultures(cultureToAdd));
    editTitle
      ? titleId && fetchMeta(titleId, null, cultureToAdd.map((x) => x.id) || [], true)
      : selectedBroadcasterId &&
        selectedPackageId &&
        fetchMeta(selectedPackageId, selectedBroadcasterId, cultureToAdd.map((x) => x.id) || [], true);
    setCultureToAdd([]);
  };

  return (
    <Stack
      sx={{ height: "87px" }}
      width="100%"
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      spacing={2}
    >
      <Stack flex={1} direction="row" alignItems="center" justifyContent="space-between">
        <Stack
          sx={{ maxWidth: "calc(75vw - 450px)" }}
          direction="row"
          alignItems="center"
          spacing={2}
          pt={1.5}
          pb={1.5}
        >
          {titleId && (
            <PosterImagePack
              imgs={[{ src: thumbnailService.GetObjectPosterUrl(titleId, ThumbnailSize.medium) }]}
              posterProps={{
                height: 52,
                width: 40,
                borderRadius: "2px",
              }}
            />
          )}
          <Stack sx={{ width: "100%" }} mr="auto">
            <Typography
              sx={{ width: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              color={theme.palette.getContrastText(theme.palette.background.default)}
              variant="subtitle2"
              fontWeight={600}
            >
              {displayedTitle}
            </Typography>
            <Button
              disabled={isLoading || isSaving || editTitle}
              sx={{ width: "110px", height: "28px", marginTop: "5px", fontSize: "12px" }}
              variant="contained"
              color="primary"
              endIcon={<ModeEditOutlinedIcon />}
              onClick={handleEditTitle}
            >
              {t("button:editTitle")}
            </Button>
          </Stack>
        </Stack>
      </Stack>
      <StyledDivider>
        <img src="/static/images/arrow-divider.svg" alt="" />
      </StyledDivider>
      {(editTitle || selectedPackageIndex >= 0) && (
        <Box
          sx={{
            width: "500px",
            marginTop: "150px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <InputLabel>{t("field:label.selectCountry")}</InputLabel>
          <Autocomplete
            sx={{ width: "250px" }}
            value={cultureToAdd}
            onChange={(e, newValue) => setCultureToAdd(newValue)}
            multiple
            id="tags-outlined"
            options={cultures
              .filter((x) =>
                editTitle ? !titleCultures.find((y) => y?.id === x.id) : !packageCultures.find((y) => y.id === x.id)
              )
              .sort((a, b) => (a.englishName || "").localeCompare(b.englishName || ""))}
            getOptionLabel={(option) => option.englishName || ""}
            defaultValue={[]}
            filterSelectedOptions
            disabled={!(selectedPackageIndex >= 0) && !editTitle}
            renderInput={(params) => (
              <OutlinedInput
                rows={1}
                {...params.InputProps}
                inputProps={params.inputProps}
                required
                sx={{ width: "100%", overflowY: "scroll", maxHeight: "50px" }}
                placeholder={t("field:placeholder.selectCountries")}
              />
            )}
          />
          <Button
            disabled={(selectedPackageIndex === -1 && !editTitle) || cultureToAdd.length === 0 || isLoading || isSaving}
            onClick={handleClickAdd}
            variant="contained"
            color="primary"
          >
            {t("common:add")}
          </Button>
          <Tooltip title={isRightPanelOpen ? t("tooltip:title.closeToolsPanel") : t("tooltip:title.openToolsPanel")}>
            <IconButton
              disabled={(!editTitle && packageCultures.length === 0) || (editTitle && titleCultures.length === 0)}
              onClick={() => dispatch(SetIsRightPanelOpen(!isRightPanelOpen))}
              color="primary"
            >
              <ListAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Stack>
  );
};

const StyledDivider = styled("figure")(({ theme }) => ({
  alignSelf: "stretch",
  img: {
    ...theme.helpers(theme).fullImage,
    width: "auto",
  },
}));

export default BundleMakerSubbar;
